/** Week-mode timesheet helpers (legacy sign-in sheet parity). */

import {
  formatAppTime,
  formatAppIntlLabel,
  getAppDisplayTimezone,
  parseDbDateTime,
  toAppDateKey,
} from "./appDateTime";

function normalizePositionBaseLabel(raw) {
  return String(raw || "Role").trim() || "Role";
}

function comparePositionLabels(a, b) {
  return normalizePositionBaseLabel(a).localeCompare(
    normalizePositionBaseLabel(b),
    undefined,
    { sensitivity: "base", numeric: true },
  );
}

function normalizeStaffLabel(raw) {
  return String(raw || "").trim();
}

function isUnassignedStaffLabel(raw) {
  const label = normalizeStaffLabel(raw).toLowerCase();
  return !label || label === "unassigned";
}

function compareStaffLabels(a, b) {
  return normalizeStaffLabel(a).localeCompare(normalizeStaffLabel(b), undefined, {
    sensitivity: "base",
    numeric: true,
  });
}

export function formatWeekDayHeader(dateStr) {
  const dateObj = parseDbDateTime(`${dateStr}T12:00:00Z`);
  if (!dateObj) {
    return { dayOfWeek: "", dayLabel: "" };
  }
  const tz = getAppDisplayTimezone();
  const monthName = formatAppIntlLabel(dateObj, { month: "long", timeZone: tz });
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "2-digit",
    day: "numeric",
  }).formatToParts(dateObj);
  const map = {};
  parts.forEach((p) => {
    if (p.type !== "literal") map[p.type] = p.value;
  });
  return {
    dayOfWeek: formatAppIntlLabel(dateObj, { weekday: "long", timeZone: tz }),
    dayLabel: `${map.day}/${monthName}/${map.year}`,
  };
}

export function formatCellTime(value) {
  return formatAppTime(value, { emptyValue: "" });
}

export function formatBreakMinutes(value) {
  if (value == null || value === "") return "";
  const minutes = Number(value);
  if (Number.isNaN(minutes)) return "";
  return String(Math.round(minutes));
}

export function timesheetRowDateKey(timesheet, fallbackDate = "") {
  const shiftStartValue =
    timesheet?.start_time ||
    timesheet?.scheduledStart ||
    timesheet?.scheduled_start ||
    timesheet?.startDate ||
    timesheet?.start_date ||
    timesheet?.checkin_time ||
    timesheet?.checkin_date ||
    timesheet?.checkinTime ||
    null;

  const dateKey = shiftStartValue ? toAppDateKey(shiftStartValue) : "";
  if (dateKey) return dateKey;
  return fallbackDate;
}

export function resolveTimesheetLocationInfo(timesheet) {
  const locationId = String(
    timesheet?.client_location_id ||
      timesheet?.location_id ||
      timesheet?.setting_client_location_id ||
      timesheet?.locationId ||
      timesheet?.clientLocationId ||
      "",
  ).trim();

  const locationName = String(
    timesheet?.client_location ||
      timesheet?.clientLocationName ||
      timesheet?.location_name ||
      timesheet?.location ||
      timesheet?.locationName ||
      timesheet?.full_address ||
      timesheet?.address ||
      timesheet?.site ||
      "",
  ).trim();

  const key = locationId || locationName || "none";
  const label = locationName || (locationId ? `#${locationId}` : "Unspecified Location");

  return { key, label, locationId, locationName };
}

export function filterTimesheetsToWeekRange(rows, weekDateRange) {
  const allowed = new Set(weekDateRange || []);
  return (rows || []).filter((ts) => allowed.has(timesheetRowDateKey(ts)));
}

export function groupTimesheetsByLocation(rows) {
  const groups = new Map();
  const order = [];
  for (const ts of rows || []) {
    const loc = resolveTimesheetLocationInfo(ts);
    if (!groups.has(loc.key)) {
      groups.set(loc.key, {
        key: loc.key,
        label: loc.label,
        locationId: loc.locationId,
        rows: [],
      });
      order.push(loc.key);
    }
    groups.get(loc.key).rows.push(ts);
  }
  return order.map((k) => groups.get(k));
}

export function resolveSupervisorsFromRows(rows) {
  const names = new Set();
  for (const ts of rows || []) {
    const candidates = [
      ts?.supervisors,
      ts?.supervisor,
      ts?.supervisor_name,
      ts?.supervisorName,
      ts?.shiftSupervisor,
    ];
    for (const c of candidates) {
      const name = String(c || "").trim();
      if (name) names.add(name);
    }
  }
  return Array.from(names).join(", ");
}

export function resolveStaffRequestIdsFromRows(rows) {
  const ids = new Set();
  for (const ts of rows || []) {
    const id = ts?.staff_request_id || ts?.staffRequestId;
    if (id) ids.add(String(id));
  }
  return Array.from(ids).join(", ");
}

function staffRowKey(ts) {
  const staffId = ts?.staff_id ?? ts?.account_id ?? ts?.staffId ?? "";
  const staffName =
    ts?.staff || ts?.temp || ts?.staffName || ts?.staff_name || "Staff";
  const role = ts?.position || ts?.role || ts?.position_name || "Role";
  return `${staffId}|${staffName}|${role}`;
}

function timesheetShiftSortValue(ts) {
  return (
    ts?.checkin_time ||
    ts?.checkin_date ||
    ts?.checkinTime ||
    ts?.start_time ||
    ts?.scheduledStart ||
    ts?.scheduled_start ||
    ""
  );
}

export function buildWeekStaffRows(rows, fallbackDate = "") {
  const staffMap = new Map();
  const order = [];

  for (const ts of rows || []) {
    const key = staffRowKey(ts);
    if (!staffMap.has(key)) {
      staffMap.set(key, {
        staffName:
          ts?.staff || ts?.temp || ts?.staffName || ts?.staff_name || "Staff",
        staffLabel: normalizeStaffLabel(
          ts?.staff || ts?.temp || ts?.staffName || ts?.staff_name || "Staff",
        ),
        roleName: ts?.position || ts?.role || ts?.position_name || "Role",
        baseRoleName: normalizePositionBaseLabel(
          ts?.position || ts?.role || ts?.position_name || "Role",
        ),
        displayRoleName: normalizePositionBaseLabel(
          ts?.position || ts?.role || ts?.position_name || "Role",
        ),
        avatarUrl:
          ts?.avatar_url ||
          ts?.profile_picture ||
          ts?.staff_avatar ||
          ts?.avatar ||
          "",
        travelTimeAmount: ts?.travel_time_amount ?? ts?.travelTimeAmount ?? null,
        travelTimeType: ts?.travel_time_type ?? ts?.travelTimeType ?? null,
        carpoolName: String(
          ts?.carpool_name ?? ts?.carpoolName ?? "",
        ).trim(),
        carpoolNote: String(
          ts?.carpool_note ?? ts?.carpoolNote ?? "",
        ).trim(),
        byDate: new Map(),
      });
      order.push(key);
    }

    const staffRow = staffMap.get(key);
    const dateKey = timesheetRowDateKey(ts, fallbackDate);
    if (!staffRow.byDate.has(dateKey)) {
      staffRow.byDate.set(dateKey, { in: [], out: [], break: [], entries: [] });
    }
    const cell = staffRow.byDate.get(dateKey);

    const inTime = formatCellTime(
      ts?.checkin_time || ts?.checkin_date || ts?.checkinTime,
    );
    const outTime = formatCellTime(
      ts?.checkout_time || ts?.checkout_date || ts?.checkoutTime,
    );
    const breakMinutes = formatBreakMinutes(
      ts?.break_minutes ??
        ts?.breakMinutes ??
        ts?.actual_unpaid_break_minutes ??
        ts?.checkin_unpaid_break_minutes ??
        ts?.checkinUnpaidBreakMinutes,
    );
    if (inTime) cell.in.push(inTime);
    if (outTime) cell.out.push(outTime);
    if (breakMinutes) cell.break.push(breakMinutes);
    cell.entries.push({
      in: inTime,
      out: outTime,
      break: breakMinutes,
      sortValue: timesheetShiftSortValue(ts),
    });
  }

  const rowsOut = order.map((k) => staffMap.get(k));
  rowsOut.forEach((row) => {
    row.byDate.forEach((cell) => {
      cell.entries.sort((a, b) => {
        const aDate = parseDbDateTime(a.sortValue);
        const bDate = parseDbDateTime(b.sortValue);
        if (aDate && bDate) return aDate - bDate;
        return String(a.sortValue || "").localeCompare(String(b.sortValue || ""));
      });
    });
  });

  rowsOut.sort((a, b) => {
    // Unassigned slots always print at the bottom of the sheet.
    const unassignedCompare =
      Number(isUnassignedStaffLabel(a.staffLabel || a.staffName)) -
      Number(isUnassignedStaffLabel(b.staffLabel || b.staffName));
    if (unassignedCompare !== 0) return unassignedCompare;
    const roleCompare = comparePositionLabels(
      a.baseRoleName || a.roleName,
      b.baseRoleName || b.roleName,
    );
    if (roleCompare !== 0) return roleCompare;
    return compareStaffLabels(
      a.staffLabel || a.staffName,
      b.staffLabel || b.staffName,
    );
  });

  const counts = new Map();
  rowsOut.forEach((row) => {
    const baseRole = normalizePositionBaseLabel(row.baseRoleName || row.roleName);
    const nextCount = (counts.get(baseRole) || 0) + 1;
    counts.set(baseRole, nextCount);
    row.displayRoleName = `${baseRole} ${nextCount}`;
  });

  return rowsOut;
}
